// TableViewport.jsx
import React, { useEffect, useRef } from 'react';
import { TableContainer } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useOverflowMask from './useOverflowMask';
import useColumnPriority from './useColumnPriority';
import { PageWidthContext } from './PageContainer';
import { useTableWidth } from './tableWidth';
import {
  COLUMN_MAX, CONTENT_WIDTH, SEARCH_MAX, TABELLE_ICON_PX, TABELLE_KNOPF_PX,
  TABELLE_ZEILENHOEHE_PX, TOUCH_TARGET_COARSE,
} from '../../theme/tokens';

// Der 44-px-Haus-Boden, den `TOUCH_TARGET_COARSE` traegt — EINE Zahl, eine
// Quelle. Das Theme setzt ihn unter `sm` in Tabellenzellen (`theme.js:703`);
// ein zweites Literal hier waere eine zweite Quelle fuer denselben Wert.
const TOUCH_TARGET_COARSE_PX = TOUCH_TARGET_COARSE['@media (pointer: coarse)'].minHeight;

/**
 * TableViewport — the scroll box a table lives in.
 *
 * @description
 *   Drop-in replacement for `<TableContainer>`: it grows into the panel and
 *   carries bottom padding so a fixed footer never overlaps the last row.
 *   Thirty-three files hand-rolled `Box overflowX: 'auto'` without it; this is
 *   the one place that knows the house rules for a scrolling table.
 *
 *   🔴 **Three G0 props, all opt-in — without them the box renders exactly as
 *   before** (UI2 Teil 10, G0.8 / BS14, 2026-10-12; Konzept
 *   `tabellen-filter-suche.md` §9.2 N9.6/§12). `stickyFirstColumn` freezes the
 *   first column while the rest scrolls, with an opaque ground under it (N9.6:
 *   `position: sticky` + covering background) — it does **not** touch the DOM
 *   order, so the reading order is unchanged. `edgeHint` fades the edge at
 *   which content really continues: it is `useOverflowMask` (BS15, G0.1) and
 *   therefore shows the mask **only** while `scrollWidth > clientWidth` — a
 *   hint that is always on is itself the lie. `columns` turns on
 *   `table-layout: fixed` with declared widths for the core columns (N9.5), so
 *   the columns stop jumping between states.
 *
 *   🔴 **N9.6 asks for `stickyFirstColumn` AND `edgeHint` together, and the two
 *   change each other.** While the first column is pinned at `left: 0`, the left
 *   edge is not a free edge: what the mask would fade is the pinned column
 *   itself, which is fully visible — the covered area lies *behind* it, at the
 *   pinned column's right border. So `stickyFirstColumn` turns the **left** mask
 *   off (`useOverflowMask`'s `leftEdge: false`); the pinned column is the left
 *   anchor. The **right** mask is unaffected — no sticky cell sits there.
 *   Probe: `TableViewport.test.jsx` V.4 (both props, scrolled right).
 *
 *   🔴 **The seam with G0b is binding** (both concepts fix it, G0 §G0.8 and
 *   `inhaltsbreite.md` §6.2, tabellen `tabellen-filter-suche.md` §12 BS14).
 *   **G0** delivers `stickyFirstColumn`, `edgeHint` and `columns` — the scroll
 *   case and the edge hint. **G0b.4** (2026-10-12) delivers `priorities`,
 *   `containerWidth` and `searchMax` — the column choice by width (der Deskriptor
 *   traegt `{ key, priority, kind }`; die Zellgrenzen `min`/`max` kommen aus
 *   **`COLUMN_MAX[kind]`**, Grill W12/§3.3:500-504 — nicht aus einem zweiten Feld
 *   am Deskriptor). Both sets are opt-in and additive.
 *
 *   🔴 **Was die G0b-Hälfte TUT — sie misst nicht, sie rechnet und meldet.** Sie
 *   nimmt die **rohe** Behälterbreite als Prop (`containerWidth`, aus
 *   `useContainerWidth`, BW4/G0b.3), ersetzt eine nicht gemessene Breite durch
 *   die **Musterbreite** der Seite (`CONTENT_WIDTH[muster] ?? CONTENT_WIDTH.reading`
 *   — §4.3:614-616, R16-W-N2; der zweite `??` ist **nicht** redundant, weil
 *   `CONTENT_WIDTH.full` `null` trägt und der Ausdruck dort durchfällt), holt sich
 *   über `useColumnPriority` (BW5) die sichtbaren Spalten, bildet daraus das
 *   **Spaltenbudget** `Σ COLUMN_MAX[kind].max` (§4.1:567) und meldet
 *   `min(Behälter, Budget)` — die **Tabellenbreite** — über `tableWidth.jsx` an
 *   den Kopf. 🔴 Gemeldet wird die **Tabellenbreite, nicht die Panelbreite**
 *   (R16-W-N4, §4.1:568, §4.3:617-618): deckelte der Kopf auf die Panelbreite,
 *   stünde sein rechtes Adornment rechts der Tabelle und **PW3** würde auf jeder
 *   budget-erschöpften `full`-Seite rot.
 *
 *   🔴 **Kein berechneter 0-Wert** (R15-Warnung 1): an `useColumnPriority` geht
 *   **nie** eine gerechnete `0` (etwa `breite − rail`). Eine **finite** Breite ist
 *   eine *angesagte* Breite und malt die schmalste Menge; „nicht gemessen"
 *   (`null`/`undefined`) trägt die Musterbreite. Der Hook wirft eine unbekannte
 *   Spaltenart deshalb nicht weg, sondern behandelt sie als `kern` — hier wird
 *   ihr fehlendes `COLUMN_MAX[kind]` mit `?? 0` aufgefangen, damit **ein**
 *   falscher Deskriptor nicht die ganze Tabelle reisst (der Hook meldet ihn
 *   bereits, `useColumnPriority.js`).
 *
 *   🔴 **Und gemeldet wird nie eine 0** (R16-Blocker 1, R15-W2). Derselbe Satz
 *   gilt für das **Ergebnis**, nicht nur für den Eingang: der Kopf nimmt jede
 *   gemeldete Zahl als Deckel an, und `0` führe ihn auf `maxWidth: 0` — die
 *   Tabelle hat dann nichts zu zeigen, der Kopf darf nicht mitsterben. Zwei
 *   Eingänge ergeben 0: ein leeres Spaltenbudget (leere Liste oder lauter
 *   unbekannte `kind`) und eine angesagte Behälterbreite `0`. Die Meldung hängt
 *   deshalb an `tabellenBreite > 0` — ohne sie bleibt der Kopf bei seinem
 *   Musterdeckel. Und die Meldung wird beim **Abbau** der Tabelle zurückgenommen
 *   (R16-W3): eine Breite ohne Gegenstand ist ein Zustand, den niemand besitzt.
 *
 *   🔴 **Der Abbau nimmt nur die EIGENE Meldung zurück** (R15-W1, R16-W1). Der
 *   Kanal trägt höchstens **eine** Meldung; jede Instanz meldet unter ihrer
 *   eigenen Kennung (`useId`), und die Rücknahme wirkt nur, wenn der Kanal **noch
 *   ihre** Meldung trägt: **die zuletzt meldende Tabelle gewinnt.** Hängt sie ab,
 *   fällt der Kanal auf `null`, auch wenn eine überschriebene noch steht. 🔴 **Je
 *   Provider meldet deshalb EINE Tabelle** (R16-R3): mehrere meldende Tabellen
 *   unter einem Provider sind ein Baufehler (`/rma/sla-overview` rendert drei);
 *   `priorities` bekommt nur die, nach der sich der Kopf richten soll. Fällt der
 *   Kanal auf `null`, kehrt der Kopf auf seinen Musterdeckel zurück, **gewollt**
 *   (R16-W2): der Kanal trägt EINE Meldung, ihr Eigentümer ist der letzte
 *   Schreiber (R15-R3-W1). Der Rückfall geschieht in **einem** Schritt; die
 *   Stufenleiter (`ViewHeader.jsx` `naechsteStufe`) gilt nur beim **Übernehmen**.
 *   Wer die Rücknahme „optimiert", nimmt den Kopf einer Tabelle mit, die fehlt.
 *
 *   🔴 **Additive means no work for the 25 consumers.** `useOverflowMask` is
 *   called unconditionally (Rules of Hooks) but is handed a **stable empty ref**
 *   while `edgeHint` is off — `ref.current` stays `null`, so the hook attaches
 *   no `scroll` listener and creates no `ResizeObserver`, and the container gets
 *   no `ref` attribute. A `ResizeObserver` per table, unconditionally, would be
 *   a behaviour change for all 25, not an addition. Dieselbe Auflage trägt die
 *   G0b-Hälfte: ohne `priorities` meldet die Tabelle **nichts** (`melde` läuft
 *   nicht), und ohne Provider ist `melde` ohnehin `null`.
 *
 * @useWhen
 *   - A MUI `<Table>` needs the house scroll box (it replaces `<TableContainer>`)
 *   - A wide table must say that columns continue past the right edge (`edgeHint`)
 *   - A report table must keep its identifier column in view while scrolling (`stickyFirstColumn`)
 *   - Core columns must keep their widths across states (`columns`)
 *   - A table on a `full` page must let its page head follow its width (`priorities` + `containerWidth`)
 *
 * @dontUseFor
 *   - A table that fits its panel — the props are then inert and only add noise
 *   - A card list under `sm` — that is `MobileRecordCard`, the other half of the same switch
 *   - A page-level horizontal overflow — that is a layout defect, not a hint
 *
 * @related
 *   - useOverflowMask — the mask logic itself; this component is one of its consumers
 *   - useColumnPriority (BW5) — turns `priorities` + the width into the visible columns
 *   - useContainerWidth (BW4) — delivers the `containerWidth` this component takes as a prop
 *   - `tableWidth.jsx` — the channel that carries the reported table width to the head
 *   - MobileRecordCard — the mobile half of the same breakpoint switch
 *   - TableFooterSimple / TableFooterHybrid — the footer the bottom padding makes room for
 *
 * @reuseHints
 *   - The three report tables on `/rma/sla-overview` are the first candidates for `stickyFirstColumn`
 *   - `edgeHint` also fits any wide comparison table once G0b decides which columns stay
 *   - The table footer should later sit on the **table** width (§4.2:599) — it reads the same number from `tableWidth.jsx`
 *
 * @props
 *   footerPadPx — bottom padding for the fixed footer (default 80)
 *   stickyFirstColumn — `false` (default) | `true`: first column `position: sticky`,
 *     `left: 0`, opaque ground; DOM order unchanged (G0). Combined with
 *     `edgeHint` it also suppresses the **left** edge mask — the pinned column
 *     is the left anchor (N9.6).
 *   stickyColumnIndex — `number` (default `1`): which **DOM** column sticks when
 *     `stickyFirstColumn` is set, 1-based. Default `1` is the old
 *     `th/td:first-of-type` rule, so every existing caller is unchanged; a list
 *     with a leading selection checkbox passes `2` to freeze the identifier
 *     instead of the checkbox (G5b-Nachbesserung 1, R15 W7 / R16 W2)
 *   🔴 **`--zeilen-ton` — der Vertrag zwischen Zeile und klebender Zelle**
 *     (G5b-Nachbesserung 2, R16-Fotobefund). Die klebende Zelle ist DECKEND
 *     (`background.paper`), sonst schienen die rollenden Zellen durch sie
 *     hindurch. Damit sie in einer farbigen Zeile nicht als weisser Block
 *     erscheint, setzt die ZEILE ihren Ton als CSS-Variable
 *     (`sx={{ '--zeilen-ton': <Farbe> }}`); `stickySx` legt ihn als Gradient
 *     über das Papier. Fehlt die Variable, ist der Gradient transparent und das
 *     Verhalten der Bestandsaufrufer unverändert. Die Variable ist der EINE
 *     Ort, an dem ein Zeilenton die klebende Spalte erreicht — Hover- oder
 *     Auswahltöne gehören in dieselbe Variable, nicht in einen zweiten
 *     Selektor.
 *     🔴 **The body cell carries its own bottom line — in its BACKGROUND, not
 *     in its border** (Nachbesserung 5, Fotobefund 940/1024 px, row PO197).
 *     With MUI's `border-collapse: collapse` the row separator belongs to the
 *     TABLE grid and the sticky cell's opaque ground painted over it in rows
 *     with fractional height, so the line started only at the second column.
 *     Nachbesserung 4 switched the pinned table to `border-collapse: separate`
 *     + `border-spacing: 0` (every cell paints its own `border-bottom`). That
 *     fixed the grid, but a `border` is a SEPARATE paint primitive: on one
 *     fractional boundary the pinned cell's border landed on a different device
 *     pixel row than the following cell's snapped ground and was covered.
 *     Nachbesserung 5 therefore paints the line as the FIRST `background-image`
 *     layer of the pinned body cell (`background-origin`/`-clip: border-box`,
 *     so it sits on the border-box bottom and not 1 px high) and makes the
 *     1-px border TRANSPARENT (keeping the box model). Background and ground
 *     now share one layer and snap together. The line is the first layer on
 *     purpose: the intended order is line first, `--zeilen-ton` second (the
 *     first layer is the topmost).
 *     🔴 **The line hangs on `tbody td:first-of-type` — it is NOT column-free.**
 *     G5b (`stickyColumnIndex`) declares its OWN `background-image` for the row
 *     tone, and CSS does not stack two declarations of the same property: one
 *     wins. At the G5b merge the selector therefore moves to
 *     `nth-of-type(${stickyColumnIndex})` and BOTH declarations are combined
 *     into ONE `background-image` list (line first, tone behind); V.2x gets a
 *     `stickyColumnIndex={2}` case (rule on cell 2, not cell 1).
 *     Header cells keep their theme border.
 *     🔴 **Row geometry is integral too** (Nachbesserung 6–9): 16 px
 *     `line-height` on every data cell (`td:not([colspan])`) and all its
 *     descendants, cell SVGs 18 px — so rows stay on whole pixels; the
 *     `colSpan` empty/error cell is excluded and keeps its typography.
 *     Without the prop this rule does not exist (`V.2c`/`V.2x` pin behaviour
 *     equality and the line's own layer)
 *     🔴 **Consumers of the row rules (2026-10-18):** `POOverview`,
 *     `ProductsTable`, `pages/SecondLifeBestand.jsx` and
 *     `pages/secondLife/OpenCases.jsx`. On the two Second-Life tables the
 *     pinned first column is the 56 px IMAGE column (`ProductThumb size={40}`,
 *     fixed height → 40 + 16 + 1 = 57 px row floor); the 16 px line rule only
 *     changes cells that already exceed that floor (three stacked lines in the
 *     item/price cell, reason + hint + button in the action cell). The image
 *     placeholder does NOT shrink: the avatar is fixed at 40 px via `size`,
 *     only its inner SVG goes 17.5 → 18 px.
 *     🔴 **Nachbesserung 13 (2026-10-18, value pulled to the measurement on
 *     2026-10-19): a text `Button` in a data cell keeps a whole-pixel height.**
 *     The 16 px line rule would pull `size="small"` to 24 px (16 + 2 × 3
 *     padding + 2 × 1 border); the rule
 *     `td:not([colspan]) .MuiButton-root { min-height: TABELLE_KNOPF_PX }`
 *     (28) holds it at 28 px, the next whole pixel above the measured 27.91.
 *     The old 32.75 figure is MUI's default (16 px root, 4 px padding) and
 *     never applied here: `theme.js:368` sets a 14 px root, and MUI v7's
 *     `sizeSmall` carries 3 px padding.
 *     Only data cells, only with `stickyFirstColumn`; the `colSpan`
 *     empty/error button keeps its height (NB9). 🔴 **The theme takes its
 *     coarse-pointer floor back in table cells from `sm`** (`theme.js:702-705`,
 *     `.MuiTableCell-root &` → `up('sm')` → `TOUCH_TARGET_COARSE_OFF`); the rule
 *     holds the button at 28 there on every pointer and leaves the theme's
 *     44 px in place below `sm` (`down('sm')`). It does NOT spread
 *     `TOUCH_TARGET_COARSE` — that would set 44 where the theme just removed it.
 *   edgeHint — `false` (default) | `true`: soft edge via `useOverflowMask`,
 *     shown **only** while the content overflows (G0). The left half of it is
 *     off while `stickyFirstColumn` is set
 *   columns — `Array<{ width?: number|string }>` (default `undefined`): turns on
 *     `table-layout: fixed` and sets the declared widths on the header cells (G0).
 *     🔴 `columns[i]` is the **DOM** column `i + 1`, not the i-th data column: a
 *     leading selection column (G0.9, concept §9.5) shifts every index by one,
 *     and the caller has to count it in
 *   priorities — `Array<{ key, priority, kind }>` (default `undefined`, G0b):
 *     the column descriptors in DOM order (`useColumnPriority`). 🔴 **Ohne diese
 *     Prop meldet die Tabelle nichts** — die G0b-Hälfte ist damit opt-in
 *   containerWidth — `number | null` (default `undefined`, G0b): the **raw**
 *     inner width of the panel, from `useContainerWidth`. A non-finite value is
 *     the page's pattern width (`CONTENT_WIDTH[muster] ?? CONTENT_WIDTH.reading`),
 *     never 0
 *   searchMax — `number | null` (default `undefined`, G0b): the cap of the Z1
 *     search field. 🔴 **It only takes effect together with `priorities`** — the
 *     channel carries ONE report, and `priorities` is what switches it on (see
 *     above); there is no second path. A caller that sets `searchMax` alone
 *     reports nothing and `ListToolbar` stays uncapped. Left out, it follows the
 *     pattern: `SEARCH_MAX` on a `full` page, none otherwise (NW4.2:648)
 *   sx — applied last, the caller wins
 */
const KEIN_REF = { current: null };

export default function TableViewport({
  footerPadPx = 80,
  stickyFirstColumn = false,
  // 🔴 Welche Spalte klebt, wenn `stickyFirstColumn` gesetzt ist — 1-basiert,
  // Vorgabe 1 (die erste). Eine Liste mit vorangestellter Auswahl-Checkbox
  // friert damit die FALLLEISTE nicht ein: die erste Zelle waere das
  // Kästchen, und die Kennung — der Grund, warum man beim Rollen die
  // Orientierung behalten will — rollte weg. Additiv: ohne die Prop bleibt
  // alles wie vorher.
  stickyColumnIndex = 1,
  edgeHint = false,
  columns,
  priorities,
  containerWidth,
  searchMax,
  sx,
  ...props
}) {
  const theme = useTheme();
  const ref = useRef(null);
  // 🔴 Der Hook laeuft UNBEDINGT (Regeln der Hooks), bekommt aber ohne
  // `edgeHint` eine stabile leere Referenz: `ref.current` bleibt `null`, der
  // Effekt steigt vor dem Zuhoerer aus, und es entsteht kein ResizeObserver.
  // Ein Beobachter je Tabelle waere keine additive Aenderung (25 Verbraucher).
  //
  // 🔴 `leftEdge: !stickyFirstColumn` — N9.6 verlangt BEIDE zusammen (klebende
  // Kennungsspalte UND Kantenhinweis). Ohne diese Zeile fade der Verlauf die
  // ersten 24 px der KLEBENDEN Spalte gegen transparent: die Maske sitzt am
  // Kasten, die klebende Zelle ist sein Nachfahre und liegt bei `left: 0` — sie
  // ist vollstaendig sichtbar und wuerde trotzdem angeschnitten. Der verdeckte
  // Bereich liegt nicht am linken Rand des Kastens, sondern HINTER der
  // klebenden Spalte (also an deren rechter Kante). Die rechte Maske bleibt:
  // dort liegt keine klebende Zelle.
  const { maskSx } = useOverflowMask(edgeHint ? ref : KEIN_REF, {
    leftEdge: !stickyFirstColumn,
  });

  // ── G0b-Hälfte (BW6, 2026-10-12) ──────────────────────────────────────────
  // 1. Das Muster der Seite — fuer den Rueckfall und den Suchdeckel.
  const muster = React.useContext(PageWidthContext);
  const { melde } = useTableWidth();
  // 2. Die Messbasis: die ROHE Behaelterbreite, sonst die Musterbreite der Seite
  //    (§4.3:614-616, R16-W-N2). 🔴 Der zweite `??` ist NICHT redundant:
  //    `CONTENT_WIDTH.full` traegt `null` (§1.1:156), der Ausdruck faellt dort
  //    also DURCH das Muster hindurch statt es zu ueberstimmen.
  const messBasis = Number.isFinite(containerWidth)
    ? containerWidth
    : CONTENT_WIDTH[muster] ?? CONTENT_WIDTH.reading;
  // 3. Die sichtbaren Spalten (BW5, rein). Ohne `priorities` ist die Menge leer —
  //    dann wird auch nichts gemeldet (s. `meldet`).
  const { visible } = useColumnPriority({ columns: priorities, containerWidth: messBasis });
  // 4. Das Spaltenbudget = Σ COLUMN_MAX[kind].max ueber die SICHTBAREN Spalten
  //    (§4.1:567). 🔴 `?? 0`: eine in `production` als `kern` behandelte Spalte
  //    mit unbekannter Art hat kein `max` — EIN falscher Deskriptor darf nicht
  //    die ganze Tabelle reissen (der Hook meldet ihn bereits).
  const budget = visible.reduce(
    (summe, spalte) => summe + (COLUMN_MAX[spalte.kind]?.max ?? 0),
    0,
  );
  // 5. Die Tabellenbreite = min(Behaelter, Budget) (§4.1:568) — NICHT die
  //    Panelbreite (R16-W-N4).
  const tabellenBreite = Math.min(messBasis, budget);
  // 6. Der Suchdeckel: eigener Wert, sonst `SEARCH_MAX` auf einer `full`-Seite
  //    (NW4.2:648). Auf `reading`/`wide` gibt es keinen Deckel (N2.9 woertlich).
  const gesuchterDeckel = searchMax ?? (muster === 'full' ? SEARCH_MAX : null);
  // 7. Gemeldet wird nur ein WERT — nie eine 0 (R16-Blocker 1, R15-W2). Der Kopf
  //    nimmt jede gemeldete Zahl als Deckel an und laeuft die Stufenleiter bis
  //    `maxWidth: 0` herunter (`ViewHeader.jsx:1072`); der Haus-Satz „nie 0"
  //    (§4.3:623-624) gilt damit fuer das ERGEBNIS, nicht nur fuer den Eingang.
  //    Zwei Eingaenge koennen hier 0 ergeben:
  //      (a) ein leeres Spaltenbudget — `priorities={[]}`, oder lauter unbekannte
  //          `kind` (der Hook reicht sie in `production` als `kern` weiter, hat
  //          aber kein `COLUMN_MAX[kind].max` — s. o. das `?? 0`); die Tabelle
  //          hat dann nichts zu zeigen, der Kopf darf nicht mitsterben;
  //      (b) eine ANGESAGTE Behaelterbreite 0 (`containerWidth={0}` ist endlich
  //          und wird genommen, §„a caller's STATED width").
  //    `tabellenBreite > 0` deckt beide ab — sie ist das Minimum aus Behaelter
  //    und Budget. Ohne Meldung bleibt der Kopf auf seinem Musterdeckel.
  const meldet = priorities !== undefined && melde != null && tabellenBreite > 0;
  // 🔴 Die EIGENE Kennung (R15-W1/R16-W1). Der Kanal traegt hoechstens EINE
  //    Meldung, und je Provider meldet EINE Tabelle (R16-R3, sonst Baufehler). Ohne
  //    Kennung naehme die abhaengende Tabelle die Meldung der anderen mit: meldet
  //    B nach A und haengt A ab, faellt der Kanal auf `null`, obwohl B lebt. Sie
  //    geht als zweites Argument an `melde`; der Provider merkt sich den
  //    Eigentuemer und nimmt nur die EIGENE Meldung zurueck. `useId` ist je
  //    Instanz stabil, auch ueber Re-Render.
  const kennung = React.useId();
  useEffect(() => {
    if (!meldet) return undefined;
    melde({ tableWidth: tabellenBreite, searchMax: gesuchterDeckel }, kennung);
    // 🔴 Abmelden beim Abbau (R16-W3). Verschwindet die Tabelle — Ladezustand,
    //    leerer Zustand, Tabwechsel innerhalb derselben Seite —, bliebe ihre
    //    zuletzt gemeldete Breite sonst im Kanal stehen, und der Kopf truege den
    //    Deckel einer Tabelle, die es nicht mehr gibt. Die Ruecknahme steht in
    //    DIESEM Effekt und nicht in einem eigenen: sie laeuft damit nur, wenn
    //    diese Tabelle wirklich gemeldet hat. 🔴 Und sie traegt die Kennung mit
    //    (R15-W1/R16-W1): eine zweite Tabelle OHNE `priorities` loescht nichts
    //    (ihr Effekt hat keine Ruecknahme), und eine zweite MIT `priorities`
    //    loescht nur ihre eigene Meldung — die zuletzt meldende gewinnt.
    //    🔴 **Vorbehalt (R16-R3, G0b.5): mehrere meldende Tabellen unter EINEM
    //    Provider sind ein BAUFEHLER; die Beschreibung hier ist das Verhalten
    //    des Netzes, keine zugesagte Lage.** Beim
    //    Wechsel einer Abhaengigkeit fallen Ruecknahme und neue Meldung in
    //    denselben Durchlauf; `melde` verrechnet beide (TableWidthProvider), es
    //    entsteht kein sichtbarer Zwischenstand.
    return () => melde(null, kennung);
  }, [meldet, melde, tabellenBreite, gesuchterDeckel, kennung]);

  // 🔴 N9.6 — die Kennungsspalte klebend, mit DECKENDEM Grund (ohne ihn
  // scheinen die rollenden Zellen durch sie hindurch). `zIndex` hebt sie ueber
  // die Nachbarzellen; der Kopf liegt eine Stufe darueber. Die Reihenfolge im
  // DOM bleibt unveraendert — `position: sticky` verschiebt nichts.
  const stickySx = stickyFirstColumn
    ? {
        // 🔴 R15/Fotobefund (Nachbesserung 4, 940/1024 px): das RAHMENMODELL,
        // nicht eine Innenkante. Nachbesserung 3 hatte den fehlenden Trenner
        // unter der klebenden Spalte als `inset`-Schatten IN die Zelle gelegt;
        // der lag aber 1 px neben der Rasterlinie der Nachbarzellen (sichtbare
        // Stufe an der Spaltengrenze, genau R16-R4-W2).
        //
        // Ursache beider Fassungen: MUI fährt `border-collapse: collapse`; die
        // Trennlinie gehört dann dem TABELLENRASTER, und der deckende Grund der
        // klebenden Zelle (`bgcolor: background.paper`, unten) malt sie in
        // Zeilen mit Bruchteil-Höhe zu.
        //
        // `separate` + `border-spacing: 0` lässt JEDE Zelle ihren eigenen
        // `border-bottom` (Theme-Rahmen der `MuiTableCell`) auf DERSELBEN
        // y-Lage tragen — die klebende Zelle eingeschlossen, ohne Schatten und
        // ohne Versatz. Der Kopf behält seinen 2-px-Theme-Rahmen; die letzte
        // Zeile trägt ihre Zelllinie wie jede andere.
        //
        // 🔴 Die STICKY-Selektoren oben (`th/td:first-of-type`) verallgemeinert
        // der offene Zweig G5b auf `nth-of-type(${stickyColumnIndex})`. Die
        // TRENNER-Regel unten haengt weiterhin an `tbody td:first-of-type` —
        // sie ist NICHT spaltenfrei. Beim G5b-Merge wandert sie mit (sonst
        // bliebe die Linie bei Index 2 auf der Auswahlspalte, und die klebende
        // Kennung saehe auf `/rma` wieder den PO197-Befund), und G5bs eigene
        // `background-image`-Deklaration (Zeilenton) wird mit dieser zu EINER
        // Liste zusammengezogen — CSS stapelt zwei Deklarationen derselben
        // Eigenschaft nicht.
        // Ohne `stickyFirstColumn` existiert dieser Block nicht —
        // `TableViewport.test.jsx` V.2c pinnt, dass der Kasten dort
        // verhaltensgleich bleibt.
        '& .MuiTable-root': { borderCollapse: 'separate', borderSpacing: 0 },
        [`& .MuiTable-root th:nth-of-type(${stickyColumnIndex}), & .MuiTable-root td:nth-of-type(${stickyColumnIndex})`]: {
          position: 'sticky',
          left: 0,
          // 🔴 Der Grund ist ZWEITEILIG, seit G5b-Nachbesserung 2 (R16,
          // Fotobefund): `background.paper` allein malte einen WEISSEN Block in
          // eine farbige Zeile — die klebende Zelle deckte den Zeilenton
          // (ungelesen = `v2Tone(info).soft`) mit Papier zu. Der Zeilenton
          // kommt deshalb als CSS-Variable von der Zeile; der Gradient legt ihn
          // ÜBER das Papier, statt es zu ersetzen. Ohne die Variable
          // (`--zeilen-ton` fehlt, alle 34 Bestandsaufrufer) ist der Gradient
          // transparent und die Zelle bleibt exakt so deckend wie vorher.
          bgcolor: 'background.paper',
          backgroundImage:
            'linear-gradient(var(--zeilen-ton, transparent), var(--zeilen-ton, transparent))',
          zIndex: 1,
        },
        // 🔴 NACHBESSERUNG 5 (Fotobefund 940/1024 px, Zeile PO197): die Linie
        // gehoert in die HINTERGRUND-Ebene der klebenden Zelle, nicht an ihren
        // `border-bottom`.
        //
        // Gemessen (g2btr, dpr 1): unter genau einer Zeilengrenze fehlte die
        // Linie in der klebenden Spalte vollstaendig, waehrend die Nachbarzelle
        // sie trug — bei 940 UND 1024, also nicht datenabhaengig. Die Unterkanten
        // beider Zellen sind gleich; es ist die ZEICHNUNG, nicht die Geometrie.
        // Ursache (Hypothese, am Kode begruendet): die klebende Zelle ist ein
        // positioniertes Element mit eigenem Stacking-Kontext (`zIndex: 1`) und
        // wird als eigene Ebene eingerastet; ihr `border-bottom` ist eine EIGENE
        // Zeichenprimitive und faellt bei gebrochenen Zeilengrenzen auf eine
        // andere Geraetepixel-Zeile als der deckende Grund der Folgezeile — der
        // deckt sie dann ab. Der `border` ist damit das falsche Werkzeug: er
        // gehoert einer anderen Ebene an als der Grund, den er ueberleben muss.
        //
        // Deshalb: die Linie als ERSTE `background-image`-Lage, auf der
        // Hintergrundflaeche des RAHMENKASTENS (`background-origin/-clip:
        // border-box` — sonst laege sie 1 px zu hoch, weil die Positionierungs-
        // flaeche sonst die Polsterbox ist; genau der Versatz, den Nachbesserung
        // 3 als `inset`-Schatten hatte). Der `border-bottom` bleibt als 1-px-
        // Kasten bestehen, wird aber DURCHSICHTIG: sonst verdoppelte sich die
        // Linie dort, wo Rahmen und Hintergrund doch auf derselben Pixelzeile
        // landen (integrierte Grenzen), und die klebende Spalte waere an diesen
        // Stellen dunkler als ihre Nachbarn.
        //
        // 🔴 REIHENFOLGE DER LAGEN (fuer G5b): `background-image` ist eine
        // Liste, die ERSTE Lage liegt OBEN. Der Trenner steht deshalb an erster
        // Stelle. 🔴 G5b deklariert seine eigene `background-image`
        // (`linear-gradient(var(--zeilen-ton, transparent), …)`) auf DERSELBEN
        // Zelle; zwei Deklarationen derselben Eigenschaft stapelt CSS NICHT,
        // eine gewinnt (heute NB5 ueber `tbody`). Der G5b-Merge muss beide
        // deshalb zu EINER Liste zusammensetzen — Trenner erste Lage, Ton
        // zweite (`linear-gradient(trenner), var(--zeilen-ton)`), damit der Ton
        // die 1-px-Linie nicht uebertoent. Der Papierton bleibt
        // `background-color` darunter, keine dritte Lage.
        [`& .MuiTable-root tbody td:nth-of-type(${stickyColumnIndex})`]: {
          borderBottomColor: 'transparent',
          // 🔴 EINE Liste, zwei Lagen: die Linie ZUERST (oberste Lage), der
          // Zeilenton dahinter. Zwei Deklarationen derselben Eigenschaft
          // stapelt CSS nicht — eine gewaenne, die andere waere weg.
          backgroundImage: (theme) =>
            `linear-gradient(to top, ${theme.palette.divider} 0 1px, transparent 1px), ` +
            'linear-gradient(var(--zeilen-ton, transparent), var(--zeilen-ton, transparent))',
          backgroundOrigin: 'border-box',
          backgroundClip: 'border-box',
        },
        [`& .MuiTable-root thead th:nth-of-type(${stickyColumnIndex})`]: { zIndex: 2 },
        // 🔴 NACHBESSERUNG 6 (2026-10-18) — GANZZAHLIGE ZEILENGEOMETRIE.
        //
        // Der Befund (Pixelprobe PW6e, `/overview` 940 px): unter EINER
        // Zeilengrenze fehlte die Linie in der klebenden Spalte, waehrend die
        // Nachbarzelle sie trug — reproduzierbar in allen sechs gemessenen
        // Konfigurationen (dpr 1/2, Viewporthoehen 900/1000/1037). Die Grenze
        // lag bei **48.156 px**: `body2`-Zeile 17.515625 + `caption`-Zeile
        // 13.640625 + 16 px Polster + 1 px Rahmen. Eine injizierte
        // `line-height` beseitigte die Luecke in allen sechs; der Wert steht
        // als `TABELLE_ZEILENHOEHE_PX` in `theme/tokens.js`.
        //
        // ⚠️ **Gemessene KORRELATION, kein bewiesener Mechanismus.** Die
        // Hypothese steht direkt darueber (eigene Ebene, deckender Grund der
        // Folgezeile); sie erklaert den Ausfall, ist aber nicht belegt. Was
        // belegt ist: gebrochene Zeilengeometrie ↔ fehlende Linie, und
        // ganzzahlige Zeilengeometrie ↔ Linie vorhanden.
        //
        // 🔴 **NACHBESSERUNG 7 (2026-10-18): `td *`, nicht `td` + Typografie,
        // und 16 statt 20 px.**
        //
        // (a) DER SELEKTOR. NB6 zog nur `.MuiTypography-root` mit; MUI setzt
        //     die Zeilenhoehe je Variante selbst (`body2` 1.43, `caption` 1.66,
        //     die Frist-Caption auf `/overview` sogar 1.3). `td *` zieht JEDEN
        //     Nachfahren mit — Typografie, Chip-Beschriftung, Link, Text im
        //     `Stack` — und macht die Textzeile 16 + 16 + 1 = 33 bzw. die
        //     Zwei-Zeilen-Zelle (body2 + caption) 16 + 16 + 16 + 1 = 49.
        //     🔴 Bausteine mit EIGENER fester `height` bleiben unberuehrt, weil
        //     `line-height` eine feste Hoehe nicht ueberschreibt: MUI-Chip
        //     `small` (24 px), der Bundle-Chip in `ProductsTable` (20 px im
        //     `sx`), `ProductThumb` (28 px ueber `size`), `IconButton` `small`
        //     (SVG 17.5 + 2 × 5 px Polster = 27.5 — den Traeger macht NB8
        //     ganzzahlig, s. u.).
        //
        //     🔴 **Die fruehere Begruendung „Pill-Fall" war falsch
        //     (Nachbesserung 8, 2026-10-18).** Hier stand, eine `Pill` ohne
        //     `MuiTypography-root`-Klasse habe die Zeilen 17/18 mit 44.5 px
        //     gebrochen. Die Browser-Messung `g2bzm` an genau dieser Zeile
        //     zeigt: der Status ist ein MUI-`Chip` `small` (feste 24 px), eine
        //     `Pill` steht dort gar nicht; das hoechste Kind ist der
        //     ⋮-`IconButton` (27.5). `line-height` kann ihn nicht erreichen —
        //     ein SVG hat keine Zeilenhöhe. Er steht deshalb in NB8.
        //
        // (b) DER WERT. 16 px haelt die Dichte des Ist-Standes: gemessen
        //     48.156 → 49 px je Zeile. 20 px ergab 57 px, also 9 px hoeher je
        //     Zeile (+18 %); 18 px ergab 53. Die Messung steht im
        //     Token-Kommentar.
        //
        // ⚠️ `th` bleibt unberuehrt: MUI traegt dort `line-height: 24 px`
        // (`pxToRem(24)`, mit `htmlFontSize: 14` ganzzahlig), und die einzige
        // Abweichung (`ProductsTable`, 1.4 × 12) ist eine bewusste
        // Kopf-Entscheidung, nicht der gemessene Gegenstand. Der Ausfall sass
        // zwischen RUMPFZEILEN.
        // 🔴 NACHBESSERUNG 9 (2026-10-18) — NUR DATENZELLEN.
        //
        // `td` traf bis hierher AUCH die Leer-/Fehlerzeile, die auf dem Desktop
        // in EINER Zelle mit `colSpan` steht (`EmptyState` samt „Try again").
        // Gemessen: deren Knopf schrumpfte von 36.5 auf 28 px (die 16-px-
        // `line-height` drueckt die Knopfzeile, die `td svg`-Regel das Symbol).
        // Das ist eine unbeabsichtigte Aenderung einer Flaeche, die mit der
        // Trennlinie nichts zu tun hat — die Regel gehoert der DATENZEILE.
        //
        // `:not([colspan])` ist der Unterschied, und er ist am Kode belegt:
        // jede Leer-/Fehlerzeile auf `/overview` (`POOverview.jsx:1790`) und
        // `/products` (`ProductsTable.jsx:2995`) traegt `colSpan`, KEINE
        // Datenzelle tut es. Die Ladezustaende rendern Skelette, keine
        // Tabellenzeile — sie sind von der Frage gar nicht beruehrt.
        //
        // ⚠️ Die Sticky- und Trenner-Regeln darueber bleiben absichtlich
        // unveraendert: sie fragen nach POSITION und Hintergrund der ersten
        // Zelle, und `position: sticky` auf einer einzelnen `colSpan`-Zelle ist
        // folgenlos (sie deckt ohnehin die ganze Zeile). Jest V.2z pinnt, dass
        // die drei Zeilen-Regeln dort NICHT greifen.
        '& .MuiTable-root td:not([colspan])': {
          lineHeight: `${TABELLE_ZEILENHOEHE_PX}px`,
        },
        '& .MuiTable-root td:not([colspan]) *': {
          lineHeight: `${TABELLE_ZEILENHOEHE_PX}px`,
        },
        // 🔴 NACHBESSERUNG 8 (2026-10-18) — GANZZAHLIGE ICONS.
        //
        // `TABELLE_ZEILENHOEHE_PX` macht Text ganzzahlig, aber nicht Icons: ein
        // MUI-`SvgIcon` mit `fontSize="small"` ist 1.25 rem × 14 = **17.5 px**
        // (es skaliert ueber `1em`). Im ⋮-Knopf (`RowActionsMenu`,
        // `IconButton size="small"`) sind das 17.5 + 2 × 5 px Polster = 27.5,
        // mit 16 px Zellpolster + 1 px Rahmen die Zeile **44.5** (gemessen:
        // `g2bzm`, `/overview` 940×1000, dpr 1, Zeile 18). `line-height` kann
        // hier nichts tun — ein SVG hat keine Zeilenhöhe.
        //
        // 18 → 28 → **45**: `TABELLE_ICON_PX` setzt die Schriftgroesse des SVG,
        // und weil MUI-`SvgIcon` `width`/`height` als `1em` fuehrt, folgt die
        // Groesse. Der Selektor ist bewusst `td:not([colspan]) svg` (nicht
        // `.MuiSvgIcon-root`):
        // er trifft jedes SVG in der Rumpfzelle, auch ein kuenftiges ohne die
        // MUI-Klasse. **`th` ist nicht dabei** — Sortier- und Erklaer-Symbole
        // im Kopf gehoeren nicht zur Rumpfzeile und bleiben unberuehrt.
        //
        // 🔴 **Die Regel bleibt bewusst BREIT** (R16-W1, entschieden
        // 2026-10-18): sie gilt jedem SVG in einer Datenzelle, nicht nur dem
        // ⋮-Knopf. Der Mechanismus (gebrochener Traeger → fehlende Linie)
        // trifft JEDES Icon-Steuerelement. Die Checkbox steht schon heute in
        // der `/rma`-Tabelle (`RMACasesDashboardBody.jsx:362` im Kopf,
        // `:1217` in der Datenzeile, beide `size="small"`; MUI rendert sie als
        // `<svg>`), 17.5 + 2 × 9 px Polster = 35.5 — dieselbe gebrochene
        // Zeilengeometrie; mit 18 px wird sie 36. G5b bringt sie NICHT: er
        // stellt die Tabelle unter `TableViewport`/die Zell-SVG-Regel, und
        // NACH dem Merge trifft die Regel sie. V.2z pinnt die Checkbox dann
        // als POSITIVEN Fall (Regel greift, Zeile ganzzahlig), nicht als
        // Ausnahme.
        // 🔴 **`/products` traegt sehr wohl ein SVG in einer Rumpfzelle:**
        // `ProductThumb` rendert als Avatar-Rueckfall `Inventory2OutlinedIcon`
        // (`ProductThumb.jsx:55`). Die Regel stellt es von 17.5 auf 18 px; die
        // Zeile bleibt unveraendert, weil der Avatar ueber `size` fest 28 px
        // hoch ist. Die fruehere Beweiszeile „auf `/products` kein Icon in
        // einer Rumpfzelle" war falsch.
        //
        // 🔴 Die Regel gewinnt gegen `MuiSvgIcon-fontSizeSmall` (1.25 rem) ueber
        // die Spezifitaet: `.css-x .MuiTable-root td:not([colspan]) svg` schlaegt eine einzelne
        // Klassenregel, unabhaengig von der Einfuegereihenfolge.
        // 🔴 NB9: auch diese Regel gilt nur der DATENZELLE. In der
        // Leer-/Fehlerzeile (`td[colspan]`) ist das Symbol Teil des
        // `EmptyState`-Knopfes und behaelt seine Groesse.
        '& .MuiTable-root td:not([colspan]) svg': {
          fontSize: `${TABELLE_ICON_PX}px`,
        },
        // 🔴 NACHBESSERUNG 13 (2026-10-18) — GANZZAHLIGE TEXT-KNOEPFE.
        //
        // Die Zeilenregel oben (`td:not([colspan]) *`) trifft auch die
        // Textzeile im MUI-`Button` und drueckt `size="small"` auf 24 px
        // (16 px Zeilenhoehe + 2 × 3 Polster + 2 × 1 Rahmen). Ein Knopf, der
        // seine Beschriftung behaelt, aber 3,91 px verliert, ist eine
        // unbeabsichtigte Aenderung der Aktionszelle —
        // `SecondLifeBestand.jsx` („Edit") und `secondLife/OpenCases.jsx`
        // („Create"/„Release"), beide `variant="outlined"`.
        //
        // 28 ist der naechste GANZE Wert zum GEMESSENEN Ist-Stand 27,91 und
        // haelt die Zeile ganzzahlig (28 + 16 + 1 = 45). Der Ist-Stand rechnet
        // sich am Kode: `theme.js:368` setzt die Wurzel auf 14 px, also
        // 0.8125rem = 11,375 px; × 1,75 = 19,90625; + 2 × 3 Polster + 2 × 1
        // Rahmen = 27,90625. Der alte Wert 32.75 war MUI's Standard bei
        // 16-px-Wurzel und 4-px-Polster und galt fuer diese Flaeche nie —
        // 32 waere 4,09 px groesser gewesen als der Knopf je war. Der Inhalt
        // sitzt mittig,
        // weil MUI's `Button`-Wurzel `display: inline-flex` mit
        // `align-items: center` fuehrt (MUIs eigene Wurzelregel, nicht hier
        // deklariert).
        //
        // 🔴 Regel 22 Nr. 8 + §4.1a: das Theme nimmt den Grobzeiger-Boden in
        // Tabellenzellen ab `sm` ausdruecklich zurueck (`theme.js:702-705`:
        // `.MuiTableCell-root &` → `up('sm')` → `TOUCH_TARGET_COARSE_OFF`,
        // also `minHeight: auto`) und setzt unter `sm` 44. Diese Regel bildet
        // das nach: ab `sm` haelt sie den Knopf auf 28 — auf JEDEM Zeiger —,
        // unter `sm` setzt sie dieselben 44 wie das Theme.
        // 🔴 Ohne den `down('sm')`-Block gewaenne sie ueber die Theme-Regel
        // (Spezifitaet (0,4,1) gegen (0,2,0), Medienabfragen erhoehen keine
        // Spezifitaet) und liesse den Knopf auch unter `sm` auf 28; ein Telefon
        // haette dann 28 statt 44 px. Sie spreadet `TOUCH_TARGET_COARSE` NICHT:
        // das setzte 44 dort, wo das Theme ihn gerade zurueckgenommen hat.
        '& .MuiTable-root td:not([colspan]) .MuiButton-root': {
          minHeight: `${TABELLE_KNOPF_PX}px`,
          [theme.breakpoints.down('sm')]: { minHeight: TOUCH_TARGET_COARSE_PX },
        },
      }
    : {};

  // 🔴 N9.5 — feste Breiten fuer die Kernspalten: `table-layout: fixed` nimmt
  // die Breiten aus der ersten Zeile (dem Kopf), deshalb stehen sie dort.
  const columnsSx = Array.isArray(columns)
    ? {
        '& .MuiTable-root': { tableLayout: 'fixed' },
        ...columns.reduce((acc, spalte, i) => {
          if (!spalte || spalte.width == null) return acc;
          acc[`& .MuiTable-root > thead > tr > th:nth-of-type(${i + 1})`] = {
            width: spalte.width,
          };
          return acc;
        }, {}),
      }
    : {};

  return (
    <TableContainer
      ref={edgeHint ? ref : undefined}
      sx={{
        flexGrow: 1,
        overflow: 'auto',
        pb: `${footerPadPx}px`,
        // Der Klebekopf deckt die Zeilen, die unter ihn scrollen. `zIndex: 6`
        // ist die Hauszahl: MUI setzt fuer `stickyHeader` nur 2, und die
        // schwebende Zeilen-Werkzeugleiste liegt bei 3 bis 5 — mit 2 malen die
        // Zeilen UEBER den Kopf. Dieselbe Zahl tragen die drei bestehenden
        // Aufrufer-Patches.
        //
        // ⚠️ Spannung zu N9.1/P32 (Konzept): der schreibt den TRANSPARENTEN Kopf
        // fest. Mit einem klebenden Kopf geht beides nicht zusammen; der
        // Ein-Schritt-Umstieg waere `stickyHeader` von der `Table` UND diesen
        // Block entfernen — eines allein ist falsch.
        '& .MuiTableCell-stickyHeader': {
          // 🔴 **`!important` ist Pflicht, nicht Kosmetik** (Regel-15-Befund B1,
          // 2026-10-12, im Browser gemessen): `theme/theme.js:510-511` setzt
          // `MuiTableCell.head { background-color: transparent !important }`.
          // Ohne das Gegen-`!important` gewinnt das Theme — die Regel hier war
          // wirkungslos, der Kopf blieb durchsichtig und die Zeilen malten
          // weiter durch ihn. Gemessen: ohne `!important` `bg=rgba(0,0,0,0)`,
          // mit `!important` `bg=rgb(255,255,255)`.
          // Dieselbe Form tragen die drei bestehenden Aufrufer-Patches —
          // sie gewinnen durch `!important`, NICHT durch Spezifitaet.
          backgroundColor: (theme) => `${theme.palette.background.paper} !important`,
          zIndex: 6,
        },
        ...stickySx,
        ...columnsSx,
        ...(edgeHint ? maskSx : {}),
        ...(sx || {}),
      }}
      {...props}
    />
  );
}
